import { PAGE_CONTAINER_MAX_WIDTH } from '../constants/documentTheme';
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Avatar,
  Grid,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../config/api';
import { SingleImageUploader } from '../components/ImageUploader';
import WhatsAppOptInCard from '../components/WhatsAppOptInCard';
import EmailAliasManager from '../components/EmailAliasManager';
import { 
  THEME_GRADIENTS, 
  THEME_SHADOWS, 
  THEME_RADIUS,
  THEME_COLORS,
  THEME_BORDERS
} from '../constants/theme';

const UserProfile = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, updateUser, logout } = useAuth();
  const [profile, setProfile] = useState({
    username: '',
    avatar: '',
    bio: '',
    first_name: '',
    last_name: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState('');
  
  // Delete account states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmUsername, setDeleteConfirmUsername] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const fetchUserProfile = useCallback(async () => {
    try {
      setError(null);
      const response = await api.get('/auth/me');
      const userData = response.data;
      setProfile({
        username: userData.username,
        email: userData.email,
        avatar: userData.avatar || '',
        bio: userData.bio || '',
        first_name: userData.first_name || '',
        last_name: userData.last_name || ''
      });
      setAvatarPreview(userData.avatar || '');
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setError('Failed to load user profile');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUserProfile();
  }, [fetchUserProfile]);

  const handleChange = useCallback((event) => {
    const { name, value } = event.target;
    setProfile(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const validateAvatarUrl = useCallback((url) => {
    if (!url) return true;
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }, []);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);

    if (!validateAvatarUrl(profile.avatar)) {
      setError('Please enter a valid URL for your avatar');
      setSaving(false);
      return;
    }

    if (!profile.username.trim()) {
      setError('Username cannot be empty');
      setSaving(false);
      return;
    }

    try {
      const response = await api.put(`/users/${user.id}`, {
        ...profile,
        avatar: profile.avatar || null
      });
      
      if (response.data) {
        setProfile(response.data);
        setAvatarPreview(response.data.avatar || '');
        updateUser(response.data);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setError(error.response?.data?.error || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  }, [profile, user.id, updateUser, validateAvatarUrl]);

  // Delete account handlers
  const handleDeleteAccountClick = useCallback(() => {
    setDeleteDialogOpen(true);
    setDeleteConfirmUsername('');
    setDeleteError(null);
  }, []);

  const handleDeleteDialogClose = useCallback(() => {
    if (!deleting) {
      setDeleteDialogOpen(false);
      setDeleteConfirmUsername('');
      setDeleteError(null);
    }
  }, [deleting]);

  const handleDeleteAccount = useCallback(async () => {
    // Validate username confirmation
    if (deleteConfirmUsername !== profile.username) {
      setDeleteError(t('profile.deleteAccountTypeName'));
      return;
    }

    setDeleting(true);
    setDeleteError(null);

    try {
      await api.delete('/auth/account');
      
      // Account deleted successfully - logout and redirect
      setDeleteDialogOpen(false);
      await logout();
      navigate('/', { 
        state: { message: t('profile.deleteAccountSuccess') }
      });
    } catch (error) {
      console.error('Error deleting account:', error);
      setDeleteError(error.response?.data?.error || t('profile.deleteAccountFailed'));
      setDeleting(false);
    }
  }, [deleteConfirmUsername, profile.username, logout, navigate, t]);

  if (loading) {
    return (
      <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          minHeight: '50vh'
        }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ py: 4 }}>
      {/* Header */}
      <Paper 
        elevation={0}
        sx={{ 
          background: THEME_GRADIENTS.primary,
          color: 'white',
          borderRadius: THEME_RADIUS.small,
          mb: 4,
          position: 'relative'
        }}
      >
        <Box sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <IconButton 
              onClick={() => navigate('/trips')}
              sx={{ 
                mr: 2,
                color: 'white',
                bgcolor: 'rgba(255, 255, 255, 0.15)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.25)',
                }
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'white' }}>
              {t('profile.title')}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Main Content with Sidebar */}
      <Grid container spacing={3}>
        {/* Main Form */}
        <Grid item xs={12} md={8}>
          <Paper 
            elevation={0}
            sx={{ 
          borderRadius: THEME_RADIUS.small,
          border: THEME_BORDERS.light,
          position: 'relative'
        }}
      >
        <form onSubmit={handleSubmit}>
          <Box sx={{ p: 4 }}>
            <Grid container spacing={4}>
              {/* Left Column - Form Fields */}
              <Grid item xs={12} md={8}>
                <Grid container spacing={3}>
                  {/* Basic Information Section */}
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      {t('profile.basicInformation')}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('profile.username')}
                      name="username"
                      value={profile.username}
                      onChange={handleChange}
                      disabled={saving}
                      required
                      placeholder="Enter your display name"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: THEME_RADIUS.small,
                        }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('profile.emailAddress')}
                      name="email"
                      value={profile.email}
                      disabled
                      placeholder="Your email address"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: THEME_RADIUS.small,
                          backgroundColor: THEME_COLORS.background.light
                        }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('profile.firstName')}
                      name="first_name"
                      value={profile.first_name}
                      onChange={handleChange}
                      disabled={saving}
                      placeholder="Enter your first name"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: THEME_RADIUS.small,
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('profile.lastName')}
                      name="last_name"
                      value={profile.last_name}
                      onChange={handleChange}
                      disabled={saving}
                      placeholder="Enter your last name"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: THEME_RADIUS.small,
                        }
                      }}
                    />
                  </Grid>
                  
                  {/* Profile Picture Section */}
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mb: 2, mt: 3, fontWeight: 600 }}>
                      {t('profile.profilePicture')}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <SingleImageUploader
                      value={profile.avatar}
                      onChange={(url) => {
                        setProfile(prev => ({ ...prev, avatar: url }));
                        setAvatarPreview(url);
                      }}
                      preset="thumbnail"
                      folder="users/avatars"
                      label={t('profile.profilePicture')}
                      height={160}
                      cropAspect={1}
                      cropShape="round"
                    />
                  </Grid>
                  
                  {/* Bio Section */}
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mb: 2, mt: 3, fontWeight: 600 }}>
                      {t('profile.aboutYou')}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t('profile.bio')}
                      name="bio"
                      value={profile.bio}
                      onChange={handleChange}
                      disabled={saving}
                      multiline
                      rows={4}
                      placeholder="Tell others about yourself and your travel interests..."
                      helperText={t('profile.bioPlaceholder')}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: THEME_RADIUS.small,
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <WhatsAppOptInCard mode="traveler" />
                    <EmailAliasManager />
                  </Grid>
                </Grid>
              </Grid>

              {/* Right Column - Profile Preview */}
              <Grid item xs={12} md={4}>
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 3, 
                    bgcolor: THEME_COLORS.background.light,
                    borderRadius: THEME_RADIUS.small,
                    border: THEME_BORDERS.light,
                    height: 'fit-content'
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                    {t('profile.profilePreview')}
                  </Typography>
                  
                  {/* Avatar Preview */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                    <Box sx={{ position: 'relative' }}>
                      <Avatar
                        src={avatarPreview}
                        sx={{ 
                          width: 100, 
                          height: 100,
                          border: `4px solid ${THEME_COLORS.primary.main}20`,
                          boxShadow: THEME_SHADOWS.medium,
                          fontSize: '2rem',
                          fontWeight: 600,
                          bgcolor: THEME_COLORS.primary.main
                        }}
                      >
                        {profile.username?.charAt(0)?.toUpperCase()}
                      </Avatar>
                    </Box>
                  </Box>

                  {/* Profile Details */}
                  {profile.username && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Username
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {profile.username}
                      </Typography>
                    </Box>
                  )}

                  {profile.email && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Email
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {profile.email}
                      </Typography>
                    </Box>
                  )}

                  {profile.first_name && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        First Name
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {profile.first_name}
                      </Typography>
                    </Box>
                  )}

                  {profile.last_name && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Last Name
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {profile.last_name}
                      </Typography>
                    </Box>
                  )}

                  {profile.bio && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Bio
                      </Typography>
                      <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                        {profile.bio}
                      </Typography>
                    </Box>
                  )}

                  {!profile.username && !profile.bio && (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                      Fill out the form to see your profile preview
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>

            {/* Action Buttons */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              pt: 4,
              mt: 4,
              borderTop: THEME_BORDERS.light
            }}>
              <Button
                onClick={() => navigate('/trips')}
                size="large"
                sx={{ 
                  px: 4,
                  borderRadius: THEME_RADIUS.small,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                {t('profile.cancel')}
              </Button>

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={saving}
                sx={{ 
                  px: 6,
                  py: 1.5,
                  borderRadius: THEME_RADIUS.small,
                  textTransform: 'none',
                  fontWeight: 700,
                  backgroundColor: THEME_COLORS.primary.main,
                  color: 'white',
                  '&:hover': {
                    backgroundColor: THEME_COLORS.primary.dark,
                    transform: 'translateY(-1px)',
                  },
                  '&:disabled': {
                    backgroundColor: THEME_COLORS.text.disabled,
                    color: 'white',
                    opacity: 0.6
                  }
                }}
              >
                {saving ? (
                  <>Saving...</>
                ) : (
                  <>{t('profile.save')}</>
                )}
              </Button>
            </Box>

            {/* Alerts */}
            {error && (
              <Alert severity="error" sx={{ mt: 3, borderRadius: THEME_RADIUS.small }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mt: 3, borderRadius: THEME_RADIUS.small }}>
                Profile updated successfully!
              </Alert>
            )}
          </Box>
        </form>
      </Paper>

      {/* Danger Zone - Account Deletion */}
      <Paper 
        elevation={0}
        sx={{ 
          mt: 3,
          p: 3,
          borderRadius: THEME_RADIUS.small,
          border: '2px solid',
          borderColor: 'error.main',
          backgroundColor: 'error.lighter'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <WarningIcon sx={{ color: 'error.main', mr: 1, fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'error.main' }}>
            {t('profile.dangerZone')}
          </Typography>
        </Box>
        
        <Divider sx={{ mb: 2 }} />
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('profile.deleteAccountWarning')}
        </Typography>
        
        <Button
          variant="outlined"
          color="error"
          startIcon={<WarningIcon />}
          onClick={handleDeleteAccountClick}
          sx={{
            borderRadius: THEME_RADIUS.small,
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: 'error.main',
              color: 'white',
              borderColor: 'error.main'
            }
          }}
        >
          {t('profile.deleteAccountButton')}
        </Button>
      </Paper>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          <Box sx={{ position: 'sticky', top: 20 }}>
            {/* Profile Quick Info */}
            <Paper 
              elevation={0}
              sx={{ 
                p: 3,
                borderRadius: THEME_RADIUS.small,
                border: THEME_BORDERS.light,
                mb: 3
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                {t('profile.quickActions')}
              </Typography>
              <Box sx={{ space: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  • {t('profile.quickActionsDesc.updateProfile')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  • {t('profile.quickActionsDesc.uploadAvatar')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • {t('profile.quickActionsDesc.managePreferences')}
                </Typography>
              </Box>
            </Paper>

            {/* Sidebar Ad */}
          </Box>
        </Grid>

      </Grid>

      {/* Delete Account Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: THEME_RADIUS.medium,
            p: 1
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', color: 'error.main' }}>
            <WarningIcon sx={{ mr: 1, fontSize: 32 }} />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {t('profile.deleteAccountConfirm')}
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Alert severity="error" sx={{ mb: 3, borderRadius: THEME_RADIUS.small }}>
            {t('profile.deleteAccountMessage')}
          </Alert>
          
          <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }}>
            {t('profile.deleteAccountTypeName')}:
          </Typography>
          
          <TextField
            fullWidth
            value={deleteConfirmUsername}
            onChange={(e) => setDeleteConfirmUsername(e.target.value)}
            placeholder={profile.username}
            disabled={deleting}
            error={deleteError && deleteConfirmUsername !== profile.username}
            helperText={deleteError}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: THEME_RADIUS.small
              }
            }}
          />
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleDeleteDialogClose}
            disabled={deleting}
            sx={{
              borderRadius: THEME_RADIUS.small,
              textTransform: 'none'
            }}
          >
            {t('profile.cancel')}
          </Button>
          <Button
            onClick={handleDeleteAccount}
            variant="contained"
            color="error"
            disabled={deleting || deleteConfirmUsername !== profile.username}
            startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : <WarningIcon />}
            sx={{
              borderRadius: THEME_RADIUS.small,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            {deleting ? 'Deleting...' : t('profile.deleteAccountButton')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserProfile; 